import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { X, Plus, Calendar } from 'lucide-react'
import { obtenerRecargoEquivalencia } from '../utils/recargoEquivalencia'

export default function FacturaIndividual({ onClose, onFacturaCreada }) {
  const modoClaro = document.body.classList.contains('modo-claro')
  const [clientes, setClientes] = useState([])
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null)
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [loading, setLoading] = useState(false)
  const [previsualizacion, setPrevisualizacion] = useState(null)
  const [serieFactura, setSerieFactura] = useState(new Date().getFullYear().toString())

  useEffect(() => {
    cargarClientes()
    cargarSerie()
    // Inicializar con semana anterior por defecto
    const fechas = calcularSemanaAnterior()
    setFechaInicio(fechas.inicio)
    setFechaFin(fechas.fin)
  }, [])

  // Auto-previsualizar cuando cambian cliente o fechas
  useEffect(() => {
    if (clienteSeleccionado && fechaInicio && fechaFin) {
      previsualizarFactura(true)
    } else {
      setPrevisualizacion(null)
    }
  }, [clienteSeleccionado, fechaInicio, fechaFin])

  async function cargarSerie() {
    const { data } = await supabase
      .from('configuracion')
      .select('serie_factura')
      .limit(1)
      .single()
    if (data?.serie_factura) setSerieFactura(data.serie_factura)
  }

  function calcularSemanaAnterior() {
    const hoy = new Date()
    const diaSemana = hoy.getDay()
    const diasHastaLunesActual = diaSemana === 0 ? 6 : diaSemana - 1
    
    const lunesActual = new Date(hoy)
    lunesActual.setDate(hoy.getDate() - diasHastaLunesActual)
    lunesActual.setHours(0, 0, 0, 0)
    
    const lunesAnterior = new Date(lunesActual)
    lunesAnterior.setDate(lunesActual.getDate() - 7)
    
    const domingoAnterior = new Date(lunesAnterior)
    domingoAnterior.setDate(lunesAnterior.getDate() + 6)
    
    return {
      inicio: lunesAnterior.toISOString().split('T')[0],
      fin: domingoAnterior.toISOString().split('T')[0]
    }
  }

  async function cargarClientes() {
    const { data, error } = await supabase
      .from('clients')
      .select('id, alias, razon_social, cif, direccion, email, recargo_equivalencia, descuento_porcentaje, periodo_facturacion')
      .order('alias')
    
    if (error) {
      console.error('Error cargando clientes:', error)
      return
    }
    
    setClientes(data || [])
  }

  async function previsualizarFactura(silencioso = false) {
    if (!clienteSeleccionado || !fechaInicio || !fechaFin) {
      if (!silencioso) alert('Selecciona un cliente y las fechas')
      return
    }

    try {
      setLoading(true)
      
      // Obtener deliveries del período
      const { data: deliveries, error: deliveriesError } = await supabase
        .from('deliveries')
        .select(`
          *,
          lineas:delivery_lines(
            *,
            producto:products(*)
          )
        `)
        .eq('client_id', clienteSeleccionado.id)
        .gte('fecha', fechaInicio)
        .lte('fecha', fechaFin)

      if (deliveriesError) throw deliveriesError

      if (!deliveries || deliveries.length === 0) {
        if (!silencioso) alert(`No hay albaranes para ${clienteSeleccionado.alias} en este período`)
        setPrevisualizacion(null)
        return
      }

      // Agrupar líneas por producto
      const lineasMap = {}
      deliveries.forEach(delivery => {
        delivery.lineas.forEach(linea => {
          const key = `${linea.product_id}-${linea.precio_unit}`
          if (!lineasMap[key]) {
            lineasMap[key] = {
              product_id: linea.product_id,
              cantidad: 0,
              precio_con_iva: linea.precio_unit,
              iva_pct: linea.iva_pct,
              descuento_aplicable: linea.descuento_aplicable,
              nombre: linea.producto?.nombre || 'Producto'
            }
          }
          lineasMap[key].cantidad += linea.cantidad_entregada - (linea.cantidad_devuelta || 0)
        })
      })

      const lineas = Object.values(lineasMap).filter(l => l.cantidad > 0)

      if (lineas.length === 0) {
        if (!silencioso) alert('No hay líneas válidas (todas son devoluciones)')
        setPrevisualizacion(null)
        return
      }

      // Calcular precios netos
      function quitarIvaRE(precioBruto, iva_pct, tieneRE) {
        const iva = iva_pct / 100
        const re = tieneRE ? (iva_pct === 10 ? 0.014 : iva_pct === 4 ? 0.005 : 0.052) : 0
        return precioBruto / (1 + iva + re)
      }

      function aplicarMargen(precioSinIvaRE, margenPct) {
        return precioSinIvaRE * (1 - margenPct / 100)
      }

      const lineasCalculadas = []
      const gruposPorIva = {}

      lineas.forEach(linea => {
        const ivaPct = Number(linea.iva_pct)
        const key = String(ivaPct)
        
        if (!gruposPorIva[key]) gruposPorIva[key] = []
        
        const cantidad = Number(linea.cantidad)
        const precioBruto = Number(linea.precio_con_iva)
        const tieneRE = Boolean(clienteSeleccionado.recargo_equivalencia)
        const margenCliente = Number(clienteSeleccionado.descuento_porcentaje || 0)
        
        const precioSinIvaRE = quitarIvaRE(precioBruto, ivaPct, tieneRE)
        const precioNeto = linea.descuento_aplicable
          ? aplicarMargen(precioSinIvaRE, margenCliente)
          : precioSinIvaRE
        
        const baseLinea = precioNeto * cantidad
        
        gruposPorIva[key].push({
          nombre: linea.nombre,
          cantidad: cantidad,
          precio_unit: precioNeto,
          base: baseLinea,
          iva_pct: ivaPct
        })
      })

      const desglosePorIva = []
      let baseTotal = 0
      let ivaTotal = 0
      let reTotal = 0

      Object.keys(gruposPorIva).sort().forEach(ivaPctKey => {
        const lineasGrupo = gruposPorIva[ivaPctKey]
        const ivaPct = Number(ivaPctKey)
        
        const baseGrupo = lineasGrupo.reduce((s, l) => s + l.base, 0)
        const ivaGrupo = baseGrupo * (ivaPct / 100)
        const rePct = clienteSeleccionado.recargo_equivalencia ? obtenerRecargoEquivalencia(ivaPct) : 0
        const reGrupo = baseGrupo * (rePct / 100)
        
        desglosePorIva.push({
          iva_pct: ivaPct,
          base: Number(baseGrupo.toFixed(2)),
          iva: Number(ivaGrupo.toFixed(2)),
          re_pct: rePct,
          re: Number(reGrupo.toFixed(2))
        })
        
        baseTotal += baseGrupo
        ivaTotal += ivaGrupo
        reTotal += reGrupo
        
        lineasGrupo.forEach(l => lineasCalculadas.push(l))
      })

      const totales = {
        base: Number(baseTotal.toFixed(2)),
        iva: Number(ivaTotal.toFixed(2)),
        re: Number(reTotal.toFixed(2)),
        total: Number((baseTotal + ivaTotal + reTotal).toFixed(2)),
        desglose_iva: desglosePorIva
      }
      
      setPrevisualizacion({
        cliente: clienteSeleccionado,
        lineas: lineasCalculadas,
        totales: totales,
        numDeliveries: deliveries.length
      })

    } catch (error) {
      console.error('Error calculando previsualización:', error)
      if (!silencioso) alert('Error: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  async function generarFactura() {
    if (!previsualizacion) {
      alert('Primero previsualiza la factura')
      return
    }

    if (!confirm(`¿Generar factura para ${clienteSeleccionado.alias}?\n\nTotal: ${formatearMoneda(previsualizacion.totales.total)}`)) {
      return
    }

    try {
      setLoading(true)

      // Obtener siguiente número de factura
      const { data: numeroData, error: errorNumero } = await supabase
        .rpc('get_next_factura_numero', { p_serie: serieFactura })

      if (errorNumero) throw new Error('No se pudo obtener número de factura')
      const numero = numeroData || 1

      // Insertar factura
      const { data: nuevaFactura, error: errorFactura } = await supabase
        .from('facturas')
        .insert({
          serie: serieFactura,
          numero: numero,
          client_id: clienteSeleccionado.id,
          cliente_nombre: clienteSeleccionado.razon_social,
          cliente_cif: clienteSeleccionado.cif,
          cliente_direccion: clienteSeleccionado.direccion,
          cliente_email: clienteSeleccionado.email,
          periodo_inicio: fechaInicio,
          periodo_fin: fechaFin,
          fecha_emision: new Date().toISOString().split('T')[0],
          tipo_periodo: clienteSeleccionado.periodo_facturacion,
          base_imponible: previsualizacion.totales.base,
          iva: previsualizacion.totales.iva,
          iva_pct: 10,
          total: previsualizacion.totales.total,
          recargo_equivalencia: previsualizacion.totales.re,
          re_pct: clienteSeleccionado.recargo_equivalencia ? 5.2 : 0,
          desglose_iva: previsualizacion.totales.desglose_iva,
          estado_verifactu: 'no_enviada'
        })
        .select()
        .single()

      if (errorFactura) throw errorFactura

      // Insertar líneas
      const lineasFactura = previsualizacion.lineas.map(l => ({
        factura_id: nuevaFactura.id,
        concepto: l.nombre,
        cantidad: l.cantidad,
        precio_unit: l.precio_unit,
        base: Number(l.base.toFixed(2)),
        iva_pct: l.iva_pct,
        total: Number(l.base.toFixed(2))
      }))

      const { error: errorLineas } = await supabase
        .from('factura_lineas')
        .insert(lineasFactura)

      if (errorLineas) throw errorLineas

      alert(`✅ Factura ${nuevaFactura.serie}-${nuevaFactura.numero} generada correctamente`)
      
      if (onFacturaCreada) {
        onFacturaCreada(nuevaFactura)
      }
      
      onClose()

    } catch (error) {
      console.error('Error generando factura:', error)
      alert('Error al generar factura: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  function formatearMoneda(cantidad) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cantidad)
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'var(--bg)',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Plus size={24} />
            <h2 style={{ margin: 0 }}>Generar Factura Individual</h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {/* Selector de cliente */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontWeight: 'bold'
            }}>
              Cliente
            </label>
            <select
              value={clienteSeleccionado?.id || ''}
              onChange={e => {
                const cliente = clientes.find(c => c.id === e.target.value)
                setClienteSeleccionado(cliente)
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                color: 'var(--fg)'
              }}
            >
              <option value="">Selecciona un cliente...</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.alias} - {cliente.razon_social}
                </option>
              ))}
            </select>
          </div>

          {/* Fechas editables */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontWeight: 'bold'
              }}>
                Fecha inicio
              </label>
              <input
                type="date"
                value={fechaInicio}
                onChange={e => setFechaInicio(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: modoClaro ? '#fff' : '#0e1116',
                  color: 'var(--fg)',
                  colorScheme: modoClaro ? 'light' : 'dark'
                }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem',
                fontWeight: 'bold'
              }}>
                Fecha fin
              </label>
              <input
                type="date"
                value={fechaFin}
                onChange={e => setFechaFin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  background: modoClaro ? '#fff' : '#0e1116',
                  color: 'var(--fg)',
                  colorScheme: modoClaro ? 'light' : 'dark'
                }}
              />
            </div>
          </div>

          {/* Indicador de carga */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
              ⏳ Calculando previsualización...
            </div>
          )}

          {/* Previsualización */}
          {previsualizacion && (
            <div style={{
              border: '2px solid var(--accent)',
              borderRadius: '8px',
              padding: '1rem',
              background: 'var(--bg-secondary)'
            }}>
              <h3 style={{ marginTop: 0 }}>
                📋 Previsualización - {previsualizacion.cliente.alias}
              </h3>
              
              <div style={{ 
                marginBottom: '1rem',
                padding: '0.75rem',
                background: 'var(--bg)',
                borderRadius: '6px'
              }}>
                <strong>Período:</strong> {fechaInicio} al {fechaFin}<br/>
                <strong>Albaranes incluidos:</strong> {previsualizacion.numDeliveries}<br/>
                <strong>Líneas:</strong> {previsualizacion.lineas.length}
              </div>

              {/* Tabla de líneas */}
              <div style={{
                maxHeight: '200px',
                overflow: 'auto',
                marginBottom: '1rem',
                border: '1px solid var(--border)',
                borderRadius: '6px'
              }}>
                <table style={{ width: '100%', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Producto</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Cant.</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>P.Unit</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previsualizacion.lineas.map((linea, i) => (
                      <tr key={i}>
                        <td style={{ padding: '0.5rem' }}>{linea.nombre}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{linea.cantidad}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{linea.precio_unit.toFixed(4)}€</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>{linea.base.toFixed(2)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totales */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}>
                <div><strong>Base imponible:</strong></div>
                <div style={{ textAlign: 'right' }}>{formatearMoneda(previsualizacion.totales.base)}</div>
                
                <div><strong>IVA:</strong></div>
                <div style={{ textAlign: 'right' }}>{formatearMoneda(previsualizacion.totales.iva)}</div>
                
                {previsualizacion.totales.re > 0 && (
                  <>
                    <div><strong>Recargo equiv.:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatearMoneda(previsualizacion.totales.re)}</div>
                  </>
                )}
                
                <div style={{ fontSize: '1.125rem', fontWeight: 'bold', paddingTop: '0.5rem', borderTop: '2px solid var(--border)' }}>
                  TOTAL:
                </div>
                <div style={{ fontSize: '1.125rem', fontWeight: 'bold', textAlign: 'right', paddingTop: '0.5rem', borderTop: '2px solid var(--border)', color: 'var(--accent)' }}>
                  {formatearMoneda(previsualizacion.totales.total)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'flex-end',
          padding: '1.5rem',
          borderTop: '1px solid var(--border)'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--bg-secondary)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--fg)'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={generarFactura}
            disabled={!previsualizacion || loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: previsualizacion && !loading ? 'var(--accent)' : 'var(--muted)',
              border: 'none',
              borderRadius: '6px',
              cursor: previsualizacion && !loading ? 'pointer' : 'not-allowed',
              color: '#000',
              fontWeight: 'bold'
            }}
          >
            ✅ Generar factura
          </button>
        </div>
      </div>
    </div>
  )
}
